import { defaultTypeResolver, Deserializer } from './deserializer';
import { logError, logWarning, nameof, parseToJSObject } from './helpers';
import { createArrayType } from './json-array-member';
import { JsonObjectMetadata, } from './metadata';
import { extractOptionBase } from './options-base';
import { defaultTypeEmitter, Serializer } from './serializer';
import { ensureTypeDescriptor, MapT, SetT } from './type-descriptor';
export { defaultTypeResolver, defaultTypeEmitter };
export class TypedJSON {
    constructor(rootConstructor, settings) {
        this.serializer = new Serializer();
        this.deserializer = new Deserializer();
        this.globalKnownTypes = [];
        this.indent = 0;
        const rootMetadata = JsonObjectMetadata.getFromConstructor(rootConstructor);
        if (rootMetadata === undefined
            || (!rootMetadata.isExplicitlyMarked && !rootMetadata.isHandledWithoutAnnotation)) {
            throw new TypeError('The TypedJSON root data type must have the @jsonObject decorator used.');
        }
        this.nameResolver = (ctor) => nameof(ctor);
        this.rootConstructor = rootConstructor;
        this.errorHandler = (error) => logError(error);
        this.config(settings);
    }
    static parse(object, rootType, settings) {
        return new TypedJSON(rootType, settings).parse(object);
    }
    static parseAsArray(object, elementType, settings, dimensions) {
        return new TypedJSON(elementType, settings).parseAsArray(object, dimensions);
    }
    static parseAsSet(object, elementType, settings) {
        return new TypedJSON(elementType, settings).parseAsSet(object);
    }
    static parseAsMap(object, keyType, valueType, settings) {
        return new TypedJSON(valueType, settings).parseAsMap(object, keyType);
    }
    static toPlainJson(object, rootType, settings) {
        return new TypedJSON(rootType, settings).toPlainJson(object);
    }
    static toPlainArray(object, elementType, dimensions, settings) {
        return new TypedJSON(elementType, settings).toPlainArray(object, dimensions);
    }
    static toPlainSet(object, elementType, settings) {
        return new TypedJSON(elementType, settings).toPlainSet(object);
    }
    static toPlainMap(object, keyCtor, valueCtor, settings) {
        return new TypedJSON(valueCtor, settings).toPlainMap(object, keyCtor);
    }
    static stringify(object, rootType, settings) {
        return new TypedJSON(rootType, settings).stringify(object);
    }
    static stringifyAsArray(object, elementType, dimensions, settings) {
        return new TypedJSON(elementType, settings).stringifyAsArray(object, dimensions);
    }
    static stringifyAsSet(object, elementType, settings) {
        return new TypedJSON(elementType, settings).stringifyAsSet(object);
    }
    static stringifyAsMap(object, keyCtor, valueCtor, settings) {
        return new TypedJSON(valueCtor, settings).stringifyAsMap(object, keyCtor);
    }
    static setGlobalConfig(config) {
        Object.assign(this._globalConfig, config);
    }
    static mapType(type, converters) {
        if (this._globalConfig.mappedTypes == null) {
            this._globalConfig.mappedTypes = new Map();
        }
        this._globalConfig.mappedTypes.set(type, converters);
    }
    config(settings) {
        settings = Object.assign(Object.assign({}, TypedJSON._globalConfig), settings);
        if (settings.knownTypes != null
            && TypedJSON._globalConfig.knownTypes != null) {
            settings.knownTypes = Array.from(new Set(settings.knownTypes.concat(TypedJSON._globalConfig.knownTypes)));
        }
        const options = extractOptionBase(settings);
        this.serializer.options = options;
        this.deserializer.options = options;
        if (settings.errorHandler != null) {
            this.errorHandler = settings.errorHandler;
            this.deserializer.setErrorHandler(settings.errorHandler);
            this.serializer.setErrorHandler(settings.errorHandler);
        }
        if (settings.replacer != null) {
            this.replacer = settings.replacer;
        }
        if (settings.typeResolver != null) {
            this.deserializer.setTypeResolver(settings.typeResolver);
        }
        if (settings.typeHintEmitter != null) {
            this.serializer.setTypeHintEmitter(settings.typeHintEmitter);
        }
        if (settings.indent != null) {
            this.indent = settings.indent;
        }
        if (settings.mappedTypes != null) {
            settings.mappedTypes.forEach((upDown, type) => {
                this.setSerializationStrategies(type, upDown);
            });
        }
        if (settings.nameResolver != null) {
            this.nameResolver = settings.nameResolver;
            this.deserializer.setNameResolver(settings.nameResolver);
        }
        if (settings.knownTypes != null) {
            settings.knownTypes.forEach((knownType, i) => {
                if (typeof knownType === 'undefined' || knownType === null) {
                    logWarning(`TypedJSON.config: 'knownTypes' contains an undefined/null value`
                        + ` (element ${i}).`);
                }
            });
            this.globalKnownTypes = settings.knownTypes;
        }
    }
    mapType(type, converters) {
        this.setSerializationStrategies(type, converters);
    }
    parse(object) {
        const json = parseToJSObject(object, this.rootConstructor);
        let result;
        try {
            result = this.deserializer.convertSingleValue(json, ensureTypeDescriptor(this.rootConstructor), this.getKnownTypes());
        }
        catch (e) {
            this.errorHandler(e);
        }
        return result;
    }
    parseAsArray(object, dimensions = 1) {
        const json = parseToJSObject(object, Array);
        return this.deserializer.convertSingleValue(json, createArrayType(ensureTypeDescriptor(this.rootConstructor), dimensions), this._mapKnownTypes(this.globalKnownTypes));
    }
    parseAsSet(object) {
        const json = parseToJSObject(object, Set);
        return this.deserializer.convertSingleValue(json, SetT(this.rootConstructor), this._mapKnownTypes(this.globalKnownTypes));
    }
    parseAsMap(object, keyConstructor) {
        const json = parseToJSObject(object, Map);
        return this.deserializer.convertSingleValue(json, MapT(keyConstructor, this.rootConstructor), this._mapKnownTypes(this.globalKnownTypes));
    }
    toPlainJson(object) {
        try {
            return this.serializer.convertSingleValue(object, ensureTypeDescriptor(this.rootConstructor));
        }
        catch (e) {
            this.errorHandler(e);
        }
    }
    toPlainArray(object, dimensions = 1) {
        try {
            return this.serializer.convertSingleValue(object, createArrayType(ensureTypeDescriptor(this.rootConstructor), dimensions));
        }
        catch (e) {
            this.errorHandler(e);
        }
    }
    toPlainSet(object) {
        try {
            return this.serializer.convertSingleValue(object, SetT(this.rootConstructor));
        }
        catch (e) {
            this.errorHandler(e);
        }
    }
    toPlainMap(object, keyConstructor) {
        try {
            return this.serializer.convertSingleValue(object, MapT(keyConstructor, this.rootConstructor));
        }
        catch (e) {
            this.errorHandler(e);
        }
    }
    stringify(object) {
        const result = this.toPlainJson(object);
        if (result === undefined) {
            return '';
        }
        return JSON.stringify(result, this.replacer, this.indent);
    }
    stringifyAsArray(object, dimensions) {
        return JSON.stringify(this.toPlainArray(object, dimensions), this.replacer, this.indent);
    }
    stringifyAsSet(object) {
        return JSON.stringify(this.toPlainSet(object), this.replacer, this.indent);
    }
    stringifyAsMap(object, keyConstructor) {
        return JSON.stringify(this.toPlainMap(object, keyConstructor), this.replacer, this.indent);
    }
    getKnownTypes() {
        const rootMetadata = JsonObjectMetadata.getFromConstructor(this.rootConstructor);
        const knownTypes = new Map();
        this.globalKnownTypes.filter(ktc => ktc).forEach(knownTypeCtor => {
            knownTypes.set(this.nameResolver(knownTypeCtor), knownTypeCtor);
        });
        if (rootMetadata !== undefined) {
            rootMetadata.processDeferredKnownTypes();
            rootMetadata.knownTypes.forEach(knownTypeCtor => {
                knownTypes.set(this.nameResolver(knownTypeCtor), knownTypeCtor);
            });
        }
        return knownTypes;
    }
    _mapKnownTypes(constructors) {
        const map = new Map();
        constructors.filter(ctor => ctor).forEach(ctor => map.set(this.nameResolver(ctor), ctor));
        return map;
    }
    setSerializationStrategies(type, converters) {
        if (converters.deserializer != null) {
            this.deserializer.setDeserializationStrategy(type, value => converters.deserializer(value, {
                fallback: (so, td) => this.deserializer.convertSingleValue(so, ensureTypeDescriptor(td), this.getKnownTypes()),
            }));
        }
        if (converters.serializer != null) {
            this.serializer.setSerializationStrategy(type, value => converters.serializer(value, {
                fallback: (so, td) => this.serializer.convertSingleValue(so, ensureTypeDescriptor(td)),
            }));
        }
    }
}
TypedJSON._globalConfig = {};
